import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { convertDisplayToDb } from "@/utils/currency";
import { useAddPayment } from "../../hooks/useAddPayment";
import { calculateTaxAmounts } from "../../services/taxCalculation.service";
import {
  type AddPaymentModalProps,
  type PaymentFormData,
  TaxCalculationSection,
  PaymentDetailsFields,
} from "./campaign-payments";

export type { AddPaymentModalProps };

type Props = AddPaymentModalProps;

export const CampaignAddPaymentModal: React.FC<Props> = ({
  open,
  onOpenChange,
  campaignInfluencers,
}) => {
  const { userCountry } = useAuth();

  const [formData, setFormData] = useState<PaymentFormData>({
    campaign_influencer_id: "",
    status: "pending",
    payment_mode: "upi",
    due_date: "",
    payment_date: "",
    invoice_number: "",
    transaction_id: "",
    notes: "",
    tds_deducted: false,
    gst_applied: false,
  });

  // Payment calculation states
  const [baseFee, setBaseFee] = useState<string>("");
  const [gstPercentage, setGstPercentage] = useState<string>("18.0");
  const [tdsPercentage, setTdsPercentage] = useState<string>("10.0");
  const [calculatedGstAmount, setCalculatedGstAmount] = useState<number>(0);
  const [calculatedTdsAmount, setCalculatedTdsAmount] = useState<number>(0);
  const [netPayable, setNetPayable] = useState<number>(0);

  const addPaymentMutation = useAddPayment();

  // Calculate GST, TDS, and net payable amounts
  useEffect(() => {
    const base = parseFloat(baseFee) || 0;
    const result = calculateTaxAmounts({
      baseFee: base,
      includeGst: formData.gst_applied,
      gstPercentage: parseFloat(gstPercentage) || 0,
      includeTds: formData.tds_deducted,
      tdsPercentage: parseFloat(tdsPercentage) || 0,
    });

    setCalculatedGstAmount(result.gstAmount);
    setCalculatedTdsAmount(result.tdsAmount);
    setNetPayable(result.netPayable);
  }, [
    baseFee,
    gstPercentage,
    tdsPercentage,
    formData.gst_applied,
    formData.tds_deducted,
  ]);

  // Clear payment date when status changes to non-paid
  useEffect(() => {
    if (formData.status !== "paid" && formData.payment_date) {
      setFormData((prev) => ({ ...prev, payment_date: "" }));
    }
  }, [formData.status, formData.payment_date]);

  const handleFieldChange = <K extends keyof PaymentFormData>(
    field: K,
    value: PaymentFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.campaign_influencer_id || !baseFee) {
      return;
    }

    const baseAmount = parseFloat(baseFee);
    const baseDbAmount = convertDisplayToDb(baseAmount, userCountry);
    const dbGstAmount = formData.gst_applied
      ? convertDisplayToDb(calculatedGstAmount, userCountry)
      : undefined;
    const dbTdsAmount = formData.tds_deducted
      ? convertDisplayToDb(calculatedTdsAmount, userCountry)
      : undefined;

    const paymentData = {
      campaign_influencer_id: formData.campaign_influencer_id,
      amount: baseDbAmount,
      status: formData.status,
      payment_mode: formData.payment_mode,
      due_date: formData.due_date || undefined,
      payment_date: formData.payment_date || undefined,
      invoice_number: formData.invoice_number || undefined,
      transaction_id: formData.transaction_id || undefined,
      notes: formData.notes || undefined,
      tds_deducted: formData.tds_deducted,
      tds_amount: dbTdsAmount,
      gst_applied: formData.gst_applied,
      gst_amount: dbGstAmount,
    };

    addPaymentMutation.mutate(paymentData, {
      onSuccess: () => {
        // Reset form
        setFormData({
          campaign_influencer_id: "",
          status: "pending",
          payment_mode: "upi",
          due_date: "",
          payment_date: "",
          invoice_number: "",
          transaction_id: "",
          notes: "",
          tds_deducted: false,
          gst_applied: false,
        });
        setBaseFee("");
        setGstPercentage("18.0");
        setTdsPercentage("10.0");
        setCalculatedGstAmount(0);
        setCalculatedTdsAmount(0);
        setNetPayable(0);
        onOpenChange(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Payment Record</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TaxCalculationSection
              baseFee={baseFee}
              onBaseFeeChange={setBaseFee}
              gstApplied={formData.gst_applied}
              onGstAppliedChange={(checked) =>
                handleFieldChange("gst_applied", checked)
              }
              gstPercentage={gstPercentage}
              onGstPercentageChange={setGstPercentage}
              calculatedGstAmount={calculatedGstAmount}
              tdsDeducted={formData.tds_deducted}
              onTdsDeductedChange={(checked) =>
                handleFieldChange("tds_deducted", checked)
              }
              tdsPercentage={tdsPercentage}
              onTdsPercentageChange={setTdsPercentage}
              calculatedTdsAmount={calculatedTdsAmount}
              netPayable={netPayable}
              userCountry={userCountry}
            />

            <PaymentDetailsFields
              formData={formData}
              onFieldChange={handleFieldChange}
              campaignInfluencers={campaignInfluencers}
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                addPaymentMutation.isPending ||
                !formData.campaign_influencer_id ||
                !baseFee
              }
              className="bg-[#A655F8] hover:bg-[#A655F8] text-white font-medium px-4 py-2 rounded-md"
            >
              {addPaymentMutation.isPending ? "Adding..." : "Add Payment"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignAddPaymentModal;
